import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateCloudVideoAssetTechnicalRequest } from "../../../../../../../../common/api/master-api/assets/details/UpdateCloudVideoAssetTechnicalRequest";
import { UpdateCloudVideoAssetTechnicalResponse } from "../../../../../../../../common/api/master-api/assets/details/UpdateCloudVideoAssetTechnicalResponse";
import { useAssetsApi } from "../../../../../../../../common/api/master-api/assets/useAssetsApi";
import { CloudAssetQueryKey } from "../../../../../common/CloudAssetQueryKey";

export interface ValidateCloudVideoAssetTechnicalParams {
    assetId?: string;
    aspectRatioId: string;
}

export interface UseEditCloudAsset {
    validateCloudVideoAssetTechnical: SprintMutationResult<ValidateCloudVideoAssetTechnicalParams, ValidationResult>;
    updateCloudVideoAssetTechnical: SprintMutationResult<
        UpdateCloudVideoAssetTechnicalRequest,
        UpdateCloudVideoAssetTechnicalResponse
    >;
}

export const useEditCloudVideoAssetTechnical = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditCloudAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateCloudVideoAssetTechnical = useSprintMutation<
        ValidateCloudVideoAssetTechnicalParams,
        ValidationResult
    >({
        mutationFn: async ({ assetId, aspectRatioId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate video asset technical details.");
            }

            return assetsApi.validateCloudVideoAssetUniqueness({ assetId, aspectRatioId });
        }
    });

    const updateCloudVideoAssetTechnical = useSprintMutation<
        UpdateCloudVideoAssetTechnicalRequest,
        UpdateCloudVideoAssetTechnicalResponse
    >({
        mutationFn: async ({ assetId, aspectRatioId, aspectTypeId }: UpdateCloudVideoAssetTechnicalRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update video asset technical details.");
            }

            return assetsApi.updateCloudVideoAssetTechnical({ assetId, aspectRatioId, aspectTypeId });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [CloudAssetQueryKey.CloudAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateCloudVideoAssetTechnical,
        updateCloudVideoAssetTechnical
    };
};
